import React, { useEffect, useRef, useState } from "react";
import Cat from "@/game/Cat";
import sfx from "@/game/sfx";
import { useTr } from "@/autoTranslate";

/**
 * Nap Timer — hold Space to sleep. Target: sleep exactly TARGET seconds.
 * Release too early or too late — miss. Great = ±0.15s, ok = ±0.4s.
 */
const TARGETS = [3.0, 5.0, 7.5, 10.0];

export default function MilosNapTimer({ onExit }) {
  const tr = useTr();
  const [best, setBest] = useState(() =>
    parseFloat(localStorage.getItem("milos_nap_best") || "999"),
  );
  const [target, setTarget] = useState(() => TARGETS[0]);
  const [state, setState] = useState("idle"); // idle | sleeping | result
  const [elapsed, setElapsed] = useState(0);
  const [lastDelta, setLastDelta] = useState(null);
  const [tier, setTier] = useState(null); // "great" | "ok" | "miss"
  const rafRef = useRef();
  const startRef = useRef(0);
  const sleepingRef = useRef(false);

  const startSleep = () => {
    if (sleepingRef.current) return;
    sleepingRef.current = true;
    setState("sleeping");
    setElapsed(0);
    startRef.current = performance.now();
    const tick = () => {
      if (!sleepingRef.current) return;
      const el = (performance.now() - startRef.current) / 1000;
      setElapsed(el);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopSleep = () => {
    if (!sleepingRef.current) return;
    sleepingRef.current = false;
    cancelAnimationFrame(rafRef.current);
    const el = (performance.now() - startRef.current) / 1000;
    const delta = Math.abs(el - target);
    setLastDelta(delta);
    let t = "miss";
    if (delta <= 0.15) t = "great";
    else if (delta <= 0.4) t = "ok";
    setTier(t);
    setState("result");
    if (t === "great") sfx.victory();
    else if (t === "ok") sfx.pop();
    else sfx.sad();
    if (delta < best) {
      setBest(delta);
      localStorage.setItem("milos_nap_best", String(delta));
    }
  };

  useEffect(() => {
    const down = (e) => {
      if (e.code === "Space" && !e.repeat) {
        e.preventDefault();
        if (state === "idle" || state === "result") startSleep();
      }
      if (e.code === "Escape") onExit && onExit();
    };
    const up = (e) => {
      if (e.code === "Space" && state === "sleeping") {
        e.preventDefault();
        stopSleep();
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [state, target]);

  const changeTarget = () => {
    const idx = TARGETS.indexOf(target);
    setTarget(TARGETS[(idx + 1) % TARGETS.length]);
    setState("idle");
    setLastDelta(null);
    setTier(null);
  };

  const tierColor =
    tier === "great" ? "var(--pink-hot)" : tier === "ok" ? "var(--gold)" : "var(--ink)";
  const tierBg =
    tier === "great" ? "var(--pink)" : tier === "ok" ? "var(--peach)" : "var(--blush)";

  return (
    <div className="w-full" data-testid="minigame-milos-nap" data-minigame-active="true">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Kedi oyunu")}
          </div>
          <h3 className="mt-1 display-serif italic text-4xl md:text-5xl">
            {tr("Şekerleme Sayacı")}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Rekor")} · {best === 999 ? "—" : `±${best.toFixed(2)}s`}
          </div>
          <button
            onClick={changeTarget}
            className="mt-1 font-mono-label text-[10px] uppercase tracking-widest border-2 border-[color:var(--ink)] rounded-full px-3 py-1 bg-[color:var(--paper)]"
            data-testid="nap-change-target"
          >
            {tr("Hedef")} · {target}s ↺
          </button>
        </div>
      </div>

      <div
        className="mt-6 relative mx-auto rounded-md border-2 border-[color:var(--ink)] flex flex-col items-center justify-center"
        style={{
          height: 320,
          maxWidth: "100%",
          background:
            "linear-gradient(180deg, var(--misty-purple) 0%, var(--pink) 100%)",
        }}
      >
        {/* Sleeping cat visual */}
        <div className="relative flex flex-col items-center">
          <div className="relative">
            <Cat dir={1} sitting={state === "sleeping"} size={110} />
            {state === "sleeping" && (
              <div className="absolute -top-2 -right-2 text-3xl font-serif-editorial italic animate-pulse">
                z<span className="text-2xl">z</span>
                <span className="text-xl">z</span>
              </div>
            )}
          </div>
          {/* pillow */}
          <div
            className="mt-1 w-40 h-6 rounded-full border-2 border-[color:var(--ink)]"
            style={{ background: "var(--paper)" }}
          />
        </div>

        <div className="mt-6 font-mono-label text-[10px] uppercase tracking-widest opacity-70">
          {tr("Geçen süre")}
        </div>
        <div className="display-serif italic text-6xl leading-none">
          {elapsed.toFixed(2)}
          <span className="text-2xl opacity-60"> s</span>
        </div>

        <div className="mt-4 flex items-center gap-2 font-mono-label text-[10px] uppercase tracking-widest opacity-70">
          <span className="kbd">{tr("Boşluk'a basılı tut")}</span>
          <span>{tr("uyu · tam")} {target}s {tr("olunca bırak")}</span>
        </div>

        {state === "result" && (
          <div
            className="mt-4 px-4 py-2 rounded-md border-2 border-[color:var(--ink)] flex items-center gap-3"
            style={{ background: tierBg }}
          >
            <span
              className="display-serif italic text-2xl"
              style={{ color: tierColor }}
              data-testid={`nap-tier-${tier}`}
            >
              {tier === "great" ? tr("Mükemmel!") : tier === "ok" ? tr("Fena değil") : tr("Şekerlemeyi kaçırdın")}
            </span>
            <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
              {tr("sapma")} ±{lastDelta.toFixed(2)}s
            </span>
          </div>
        )}

        {state !== "sleeping" && (
          <div className="mt-4 flex gap-2">
            <button className="btn-pill warm" onClick={startSleep} data-testid="nap-start">
              {tr("Uyu")}
            </button>
            <button className="btn-pill ghost" onClick={onExit} data-testid="nap-exit">
              {tr("Çık")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
